"use client"

import {
  BadgeCheck,
  Bell,
  ChevronsUpDown,
  CreditCard,
  LogOut,
  Sparkles,
  Heart,
} from "lucide-react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "./avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "./sidebar"

export function NavUser({
  user,
  onLogout,
  onAccountClick,
  onBillingClick,
  onPreferencesClick,
  onHealthProfileClick,
  userRole = "patient",
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
  onLogout?: () => void
  onAccountClick?: () => void
  onBillingClick?: () => void
  onPreferencesClick?: () => void
  onHealthProfileClick?: () => void
  userRole?: "patient" | "admin" | "provider"
}) {
  const { isMobile } = useSidebar()

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="rounded-lg">
                  {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg bg-white text-gray-900 border-gray-200"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="rounded-lg !bg-gray-200 !text-gray-800">
                    {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold !text-gray-900">{user.name}</span>
                  <span className="truncate text-xs !text-gray-600">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="!bg-gray-200" />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={onAccountClick} className="hover:!bg-gray-100 hover:!text-gray-900 !text-gray-900">
                <BadgeCheck />
                Account
              </DropdownMenuItem>
              {userRole === "patient" && (
                <DropdownMenuItem onClick={onHealthProfileClick} className="hover:!bg-gray-100 hover:!text-gray-900 !text-gray-900">
                  <Heart />
                  Health Profile
                </DropdownMenuItem>
              )}
              {userRole === "patient" && (
                <DropdownMenuItem onClick={onBillingClick} className="hover:!bg-gray-100 hover:!text-gray-900 !text-gray-900">
                  <CreditCard />
                  Billing
                </DropdownMenuItem>
              )}
              {userRole === "patient" && (
                <DropdownMenuItem onClick={onPreferencesClick} className="hover:!bg-gray-100 hover:!text-gray-900 !text-gray-900">
                  <Bell />
                  Preferences
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator className="!bg-gray-200" />
            <DropdownMenuItem onClick={onLogout} className="hover:!bg-gray-100 hover:!text-gray-900 !text-gray-900">
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}